import { Pic } from "./Pic";

import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Landmark, Trees, Layers } from "lucide-react";
import { MaskLines, Reveal, SectionLabel } from "./Motion";
import { useT } from "@/i18n";

const RECORDS = [
  { id: "r1", icon: Landmark, title: "Pionieri nel restauro", text: "Una delle primissime realtà in Italia specializzate nel restauro artistico e monumentale di pregio." },
  { id: "r2", icon: Trees, title: "Primi nel legno lamellare", text: "La prima azienda in assoluto a produrre legno lamellare nel Sud Italia." },
  { id: "r3", icon: Layers, title: "Pionieri dell'XLAM (CLT)", text: "La prima azienda produttrice di pannelli in XLAM nel Meridione: il futuro delle costruzioni in bioedilizia." },
];

const EVENTS = [
  { id: "e1", year: "1905", img: "/img/emma/giuseppe-senior-ritratto.jpg", ratio: "aspect-[3/4] md:max-w-sm md:ml-auto", imgAlt: "Ritratto di Giuseppe Emma Senior (1878–1948), prima generazione", imgCap: "Giuseppe Emma Senior · 1ª generazione", title: "Il capostipite", text: "Nel 1905 tutto ha inizio a San Cataldo (CL) con Giuseppe Salvatore Emma (1878–1948), affettuosamente noto come “Peppinu 'u zannu”. Appresa la raffinata tecnica “mista” nella bottega del celebre maestro Francesco Biangardi, si distingue da subito come scultore e restauratore." },
  { id: "e2", year: "1924–1946", title: "Le sei Varicedde", text: "Realizza sei storiche Varicedde, opere d'arte sacra centrali nella tradizione locale." },
  { id: "e3", year: "1948", img: "/img/emma/seconda-generazione.jpg", imgAlt: "Giuseppe Emma (1920–1988), seconda generazione, nella sua bottega", title: "La bottega passa al figlio", text: "Giuseppe Emma (1920–1988), autore di celebri opere e stimato restauratore delle grandi Vare del Giovedì Santo. L'eredità passa poi ai suoi figli: l'azienda si afferma come una delle prime e più autorevoli realtà di restauro artistico e monumentale, con interventi storici su patrimoni culturali di inestimabile valore." },
  { id: "e3b", year: "1960–1970", title: "Pioniere del restauro monumentale", text: "Salvatore Emma inizia come restauratore di stucchi dipinti e di paramenti lapidei e fonda una delle realtà più importanti della Sicilia specializzata nel restauro monumentale e artistico di arte sacra." },
  { id: "e4", year: "2001", title: "La rivoluzione del lamellare", text: "Salvatore Emma fonda la Intral sas e realizza il primo e più grande impianto del Sud Italia per la produzione di legno lamellare. Con tecnologie a controllo numerico (CNC) ottiene la Certificazione Austriaca di Categoria A per elementi in lamellare per grandi luci, rilasciata dall'MPA Stuttgart Otto-Graf-Institut." },
  { id: "e5", year: "2018", title: "Nasce Emma Industrie srl", text: "Con l'ingresso della quarta generazione nasce Emma Industrie srl, forte di oltre 60 anni di esperienza in appalti pubblici e privati. Un altro primato: è la prima azienda produttrice di XLAM (CLT) del Sud Italia." },
  { id: "e6", year: "Oggi", title: "Un'unica famiglia, in tutta Europa", text: "Unico interlocutore tecnico ed energetico per il cliente, affiancato da collaboratori storici con oltre 20 anni di esperienza, veri membri della famiglia Emma. Serviamo cantieri pubblici, complessi industriali e progetti privati in tutta Europa." },
];

const Records = ({ t }) => (
  <div className="mt-16 grid gap-4 md:grid-cols-3">
    {RECORDS.map((r, i) => (
      <Reveal key={r.id} delay={i * 0.1} className="group border border-white/10 bg-[#1B1816] p-8 transition-[border-color] duration-500 hover:border-[#D98A52]/60" data-testid={`history-record-${r.id}`}>
        <div className="flex items-center justify-between">
          <r.icon className="h-9 w-9 text-[#D98A52] transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110" strokeWidth={1.4} />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#A39E93]">{t("hist.record", "Primato")} 0{i + 1}</span>
        </div>
        <h3 className="mt-8 font-display text-2xl font-semibold tracking-tight">{t(`hist.${r.id}.title`, r.title)}</h3>
        <p className="mt-3 text-base leading-relaxed text-[#A39E93]">{t(`hist.${r.id}.text`, r.text)}</p>
      </Reveal>
    ))}
  </div>
);

const Event = ({ e, i, t }) => (
  <motion.li
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    className={`relative pl-10 md:w-1/2 md:pl-0 ${i % 2 ? "md:ml-auto md:pl-14" : "md:pr-14 md:text-right"}`}
    data-testid={`history-event-${e.id}`}
  >
    <span className={`absolute left-[-7px] top-3 h-3.5 w-3.5 rounded-full border-2 border-[#D98A52] bg-[#121110] ${i % 2 ? "md:left-[-7px]" : "md:left-auto md:right-[-7px]"}`} />
    <p className="font-display text-5xl font-extrabold tracking-tight text-[#D98A52] sm:text-6xl" style={{ fontStretch: "75%" }}>{t(`hist.${e.id}.year`, e.year)}</p>
    <h3 className="mt-3 font-display text-2xl font-semibold tracking-tight">{t(`hist.${e.id}.title`, e.title)}</h3>
    <p className="mt-3 text-base leading-relaxed text-[#A39E93]">{t(`hist.${e.id}.text`, e.text)}</p>
    {e.img && (
      <figure className="mt-8" data-testid={`history-photo-${e.id}`}>
        <motion.div
          initial={{ clipPath: "inset(10% 10% 10% 10%)" }}
          whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
          className={`${e.ratio ?? "aspect-[4/3]"} overflow-hidden`}
        >
          <Pic src={e.img} alt={t(`hist.${e.id}.imgAlt`, e.imgAlt)} loading="lazy" className="h-full w-full object-cover sepia-[0.15] transition-[filter] duration-700 hover:sepia-0" />
        </motion.div>
        <figcaption className="mt-3 font-mono text-xs uppercase tracking-[0.18em] text-[#D98A52]">{t(`hist.${e.id}.imgCap`, e.imgCap ?? "Giuseppe Emma · 2ª generazione")}</figcaption>
      </figure>
    )}
  </motion.li>
);

export const History = () => {
  const t = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  const scaleY = useSpring(scrollYProgress, { stiffness: 90, damping: 25 });

  return (
    <section id="storia" className="grain relative bg-[#121110] px-5 py-28 text-[#F7F5F0] sm:px-8 lg:py-40" data-testid="history-section">
      <div className="mx-auto max-w-[1500px]">
        <SectionLabel index="1905" dark>{t("hist.label", "Tre primati storici")}</SectionLabel>
        <MaskLines
          className="mt-10 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-[4.2rem]"
          lines={[t("hist.h1", "Dal restauro d'arte"), <span key="h">{t("hist.h2a", "all'avanguardia")} <em className="font-serif font-normal text-[#D98A52]">{t("hist.h2em", "industriale.")}</em></span>]}
        />
        <Records t={t} />
        <div ref={ref} className="relative mx-auto mt-28 max-w-5xl">
          <div className="absolute bottom-0 left-0 top-0 w-px bg-white/10 md:left-1/2" />
          <motion.div style={{ scaleY }} className="absolute bottom-0 left-0 top-0 w-px origin-top bg-[#D98A52] md:left-1/2" />
          <ol className="space-y-16 md:space-y-20">
            {EVENTS.map((e, i) => <Event key={e.id} e={e} i={i} t={t} />)}
          </ol>
        </div>
      </div>
    </section>
  );
};
